import { CheckCircle2, Circle, AlertTriangle } from "@/components/icons";
import {
  DEAL_PIPELINE_STEPS,
  getPipelineStep,
  pipelineStepLabel,
  getPipelineStepIndex,
  type DealLifecycleState,
  type DealPipelineStep,
} from "@shared/deal/deal-lifecycle";
import { getDealExceptionLabel } from "@shared/deal/deal-exception-label";
import { cn } from "@/lib/utils";

interface DealStepperProps {
  state: DealLifecycleState;
  steps?: ReadonlyArray<DealPipelineStep>;
  className?: string;
  variant?: "full" | "compact";
}

export function DealStepper({
  state,
  steps = DEAL_PIPELINE_STEPS,
  className,
  variant = "full",
}: DealStepperProps) {
  if (state.isException) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive-bg px-3 py-2 text-sm text-destructive-strong",
          className,
        )}
      >
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
        <span className="font-medium">
          {getDealExceptionLabel(state.stage, state.exceptionKind)}
        </span>
      </div>
    );
  }

  const currentStep = pickPipelineStep(state);
  const currentIndex = getPipelineStepIndex(currentStep);

  if (variant === "compact") {
    return (
      <div className={cn("flex flex-col gap-2", className)}>
        <div className="flex items-baseline justify-between gap-2 text-xs">
          <span className="font-semibold text-foreground">
            {pipelineStepLabel(currentStep, state)}
          </span>
          <span className="text-muted-foreground">
            Step {currentIndex + 1} of {steps.length}
          </span>
        </div>
        <div
          className="flex gap-1"
          role="progressbar"
          aria-valuenow={currentIndex + 1}
          aria-valuemin={1}
          aria-valuemax={steps.length}
          aria-label={pipelineStepLabel(currentStep, state)}
        >
          {steps.map((step) => {
            const stepIndex = getPipelineStepIndex(step);
            return (
              <span
                key={step}
                className={cn(
                  "h-1.5 flex-1 rounded-full",
                  stepIndex < currentIndex && "bg-success",
                  stepIndex === currentIndex && "bg-action-fill",
                  stepIndex > currentIndex && "bg-muted",
                )}
              />
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <ol
      className={cn(
        "grid w-full grid-cols-3 gap-2 rounded-md border border-border bg-card p-3 text-xs sm:grid-cols-6",
        className,
      )}
    >
      {steps.map((step) => {
        const stepIndex = getPipelineStepIndex(step);
        const isComplete = stepIndex < currentIndex;
        const isCurrent = stepIndex === currentIndex;
        return (
          <li
            key={step}
            className={cn(
              "flex items-center gap-1.5",
              isCurrent && "font-semibold text-foreground",
              isComplete && "text-muted-foreground",
              !isCurrent && !isComplete && "text-muted-foreground/60",
            )}
            aria-current={isCurrent ? "step" : undefined}
          >
            {isComplete ? (
              <CheckCircle2
                className="h-3.5 w-3.5 shrink-0 text-success-strong"
                aria-hidden
              />
            ) : (
              <Circle
                className={cn(
                  "h-3.5 w-3.5 shrink-0",
                  isCurrent ? "text-action" : "text-muted-foreground/40",
                )}
                aria-hidden
              />
            )}
            <span className="truncate">{pipelineStepLabel(step, state)}</span>
          </li>
        );
      })}
    </ol>
  );
}

function pickPipelineStep(state: DealLifecycleState): DealPipelineStep {
  return getPipelineStep(state);
}
